/**
 * Canonical form of an email address before hashing. Shared rule with the site lib
 * (cookieless-world.js) and the Salesforce import job (email_hashed_norm): Unicode NFKC,
 * every whitespace character removed (including zero-width), lower-cased.
 */
export function normaliseEmail(value: string | null | undefined): string {
  return String(value ?? '')
    .normalize('NFKC')
    .replace(/[\s\u200B-\u200D\uFEFF]+/g, '')
    .toLowerCase();
}

async function sha256Hex(input: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(input);

  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** SHA-256 of the canonical email form; '' for an empty address. */
export async function digestEmailSHA256(
  email: string | null | undefined
): Promise<string> {
  const canonical = normaliseEmail(email);
  return canonical ? sha256Hex(canonical) : '';
}

/**
 * Generic hash for identity fields (emailHashed / phoneHashed). An input containing "@" is an
 * email address and is canonicalised first so every caller matches digestEmailSHA256; other
 * values (phones) are hashed as given.
 */
export async function digestStringSHA256(input: string): Promise<string> {
  if (input && input.indexOf('@') !== -1) {
    return digestEmailSHA256(input);
  }
  return sha256Hex(input);
}
